import { Link, usePage, router } from '@inertiajs/react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { SharedProps } from '@/types'

// 主导航：每天用的核心循环
const TABS = [
  { href: '/dashboard', label: '📊 看板' },
  { href: '/checkin', label: '✅ 打卡' },
  { href: '/goals', label: '🎯 目标' },
  { href: '/store', label: '🛍️ 商店' },
]

export default function Layout({ children }: { children: ReactNode }) {
  const page = usePage<SharedProps>()
  const { people, current_person, current_user, flash } = page.props
  const url = page.url

  const isActive = (href: string) => url.startsWith(href)

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!menuOpen) return
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [menuOpen])

  const initial = current_person?.name?.[0] ?? '?'

  return (
    <div className="min-h-screen">
      <div className="sticky top-0 z-20">
        <header className="bg-white border-b border-[#e7ebf2] px-4 h-14 flex items-center gap-2">
          <span className="font-extrabold text-[17px] bg-gradient-to-r from-[#5b7cfa] to-[#8a5bfa] bg-clip-text text-transparent whitespace-nowrap mr-1">
            🎯<span className="hidden sm:inline"> HabitPoint</span>
          </span>

          <nav className="flex items-center gap-0.5 overflow-x-auto no-scrollbar -mx-1 px-1 flex-1">
            {TABS.map((t) => (
              <Link
                key={t.href}
                href={t.href}
                className={
                  'px-3 py-1.5 rounded-lg font-semibold whitespace-nowrap text-sm ' +
                  (isActive(t.href)
                    ? 'bg-[#eef2ff] text-[#5b7cfa]'
                    : 'text-[#707d90] hover:text-[#1b2330] hover:bg-[#f4f6fb]')
                }
              >
                {t.label}
              </Link>
            ))}
          </nav>

          {current_person && !current_person.can_write && (
            <span
              title={`你对「${current_person.name}」只有只读权限`}
              className="bg-[#fff4e5] text-[#b86e00] font-semibold px-2.5 py-1.5 rounded-full text-[13px] whitespace-nowrap shrink-0"
            >
              👁<span className="hidden sm:inline"> 只读</span>
            </span>
          )}

          <div className="bg-[#eef2ff] text-[#5b7cfa] font-bold px-3 py-1.5 rounded-full text-sm whitespace-nowrap shrink-0">
            💰 {current_person?.balance ?? 0}
          </div>

          <div className="relative shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full border border-[#e7ebf2] hover:border-[#5b7cfa]"
            >
              <span className="w-7 h-7 rounded-full grid place-items-center bg-gradient-to-br from-[#5b7cfa] to-[#8a5bfa] text-white text-sm font-bold">
                {initial}
              </span>
              <span className="text-sm font-semibold hidden sm:inline">{current_person?.name ?? '我的'}</span>
              <span className="text-[#707d90] text-xs">▾</span>
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-[#e7ebf2] rounded-xl shadow-[0_1px_2px_rgba(20,30,50,.05),0_8px_28px_rgba(20,30,50,.06)] py-1 text-sm">
                {people.length > 1 && (
                  <>
                    <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold text-[#9aa6b8] uppercase tracking-wide">切换人员</div>
                    {people.map((p) => {
                      const active = p.id === current_person?.id
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setMenuOpen(false)
                            if (!active) router.post(`/people/${p.id}/switch`)
                          }}
                          className={
                            'w-full text-left px-3 py-2 flex items-center gap-2 ' +
                            (active ? 'bg-[#f4f6fb] font-semibold' : 'hover:bg-[#f4f6fb]')
                          }
                        >
                          <span className="w-5 h-5 rounded-full grid place-items-center bg-[#5b7cfa] text-white text-[11px]">{p.name[0]}</span>
                          {p.name}
                          {active && <span className="ml-auto text-[#5b7cfa]">✓</span>}
                        </button>
                      )
                    })}
                    <div className="h-px bg-[#e7ebf2] my-1" />
                  </>
                )}

                <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold text-[#9aa6b8] uppercase tracking-wide">浏览</div>
                <Link href="/badges" onClick={() => setMenuOpen(false)} className="block px-3 py-2 hover:bg-[#f4f6fb]">🏅 成就</Link>
                <Link href="/ledger" onClick={() => setMenuOpen(false)} className="block px-3 py-2 hover:bg-[#f4f6fb]">📒 流水</Link>

                <div className="h-px bg-[#e7ebf2] my-1" />
                <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold text-[#9aa6b8] uppercase tracking-wide">管理</div>
                <Link href="/people" onClick={() => setMenuOpen(false)} className="block px-3 py-2 hover:bg-[#f4f6fb]">👥 人员管理</Link>
                {current_user && (
                  <Link href="/account/edit" onClick={() => setMenuOpen(false)} className="block px-3 py-2 hover:bg-[#f4f6fb]">
                    ⚙️ 账号设置
                    <span className="text-[#9aa6b8] text-xs block">{current_user.email_address}</span>
                  </Link>
                )}

                {current_user && (
                  <>
                    <div className="h-px bg-[#e7ebf2] my-1" />
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false)
                        router.delete('/session')
                      }}
                      className="w-full text-left px-3 py-2 text-[#c0392b] hover:bg-[#fdecec]"
                    >
                      ↩ 退出登录
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </header>
      </div>
      <main className="max-w-[960px] mx-auto px-4 py-5 grid gap-4">
        {flash?.notice && (
          <div className="text-sm rounded-[10px] px-3 py-2 bg-[#e9f9ef] text-[#1d7a44] border border-[#bdeccd]">{flash.notice}</div>
        )}
        {flash?.alert && (
          <div className="text-sm rounded-[10px] px-3 py-2 bg-[#fdecec] text-[#c0392b] border border-[#f5c6c6]">{flash.alert}</div>
        )}
        {children}
      </main>
    </div>
  )
}

export function Card({ title, extra, children }: { title?: ReactNode; extra?: ReactNode; children: ReactNode }) {
  return (
    <section className="bg-white border border-[#e7ebf2] rounded-2xl shadow-[0_1px_2px_rgba(20,30,50,.05),0_8px_28px_rgba(20,30,50,.06)]">
      {title && (
        <div className="px-[18px] py-[15px] border-b border-[#e7ebf2] flex items-center gap-2.5">
          <h2 className="text-[15px] font-bold m-0">{title}</h2>
          <div className="flex-1" />
          {extra}
        </div>
      )}
      <div className="px-[18px] py-4">{children}</div>
    </section>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="text-[#707d90] text-center py-6 text-sm">{children}</div>
}
